// src/components/PortfolioHeatmap/PortfolioHeatmap.tsx

import React, { useState } from 'react';
import type { PortfolioHeatmapData } from '../../services/AlpacaService';
import styles from './PortfolioHeatmap.module.css';

/* ── Color helpers ── */

/** Blue (negative) → White (0) → Red (positive correlation) */
function corrColor(val: number, isDiag: boolean): string {
  if (isDiag) return 'rgba(100, 100, 100, 0.15)';
  // Normalize from [-1, 1] to [0, 1]
  const t = (val + 1) / 2;
  if (t < 0.5) {
    // Blue to white
    const s = t * 2;
    const r = Math.round(60 + s * 195);
    const g = Math.round(100 + s * 155);
    const b = Math.round(220 - s * 20);
    return `rgba(${r}, ${g}, ${b}, 0.45)`;
  } else {
    // White to red
    const s = (t - 0.5) * 2;
    const r = 220;
    const g = Math.round(220 - s * 160);
    const b = Math.round(200 - s * 140);
    return `rgba(${r}, ${g}, ${b}, 0.45)`;
  }
}

const SECTOR_COLORS: Record<string, string> = {
  Technology: '#3b82f6',
  Semiconductors: '#6366f1',
  'AI & Cloud': '#8b5cf6',
  Cybersecurity: '#a855f7',
  Energy: '#f59e0b',
  'Natural Gas': '#fbbf24',
  Financials: '#10b981',
  Healthcare: '#14b8a6',
  Biotech: '#06b6d4',
  Defense: '#64748b',
  Industrials: '#78716c',
  'Consumer Discretionary': '#ec4899',
  'Consumer Staples': '#f472b6',
  'Crypto-Adjacent': '#f97316',
  'EV & Clean Energy': '#22c55e',
  'Precious Metals': '#eab308',
  Shipping: '#0ea5e9',
  Utilities: '#94a3b8',
};

function sectorColor(sector: string): string {
  return SECTOR_COLORS[sector] || '#6b7280';
}

/* ── Main Component ── */

interface PortfolioHeatmapProps {
  data: PortfolioHeatmapData;
}

const PortfolioHeatmap: React.FC<PortfolioHeatmapProps> = ({ data }) => {
  const [tooltip, setTooltip] = useState<{
    symA: string;
    symB: string;
    val: number;
    px: number;
    py: number;
  } | null>(null);

  const { symbols, sectors, matrix, sectorExposure, riskWarnings, clusteredGroups } = data;

  // Sort sector exposure by pct descending
  const sortedSectors = Object.entries(sectorExposure).sort((a, b) => b[1].pct - a[1].pct);
  const maxPct = sortedSectors.length > 0 ? Math.max(sortedSectors[0][1].pct, 40) : 40;

  return (
    <div className={styles.container}>
      {/* ── Risk Warnings ── */}
      {riskWarnings.length > 0 && (
        <div className={styles.warnings}>
          {riskWarnings.map((w, i) => (
            <div key={i} className={`${styles.warning} ${w.includes('HIGH') ? styles.warningHigh : styles.warningMed}`}>
              {w.includes('HIGH') ? '🔴' : '🟡'} {w}
            </div>
          ))}
        </div>
      )}

      {/* ── Correlation Matrix ── */}
      {symbols.length >= 2 && (
        <div className={styles.matrixWrap}>
          <h3 className={styles.matrixTitle}>Position Correlation Matrix</h3>
          <table className={styles.matrix}>
            <thead>
              <tr>
                <th className={styles.cornerCell} />
                {symbols.map((s) => (
                  <th key={s} className={styles.headerCell}>
                    {s}
                    <span className={styles.sectorLabel}>{sectors[s]}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {symbols.map((rowSym, i) => (
                <tr key={rowSym}>
                  <td className={styles.rowHeader}>
                    {rowSym}
                    <span className={styles.sectorLabel}>{sectors[rowSym]}</span>
                  </td>
                  {symbols.map((colSym, j) => {
                    const val = matrix[i]?.[j] ?? 0;
                    const isDiag = i === j;
                    return (
                      <td
                        key={colSym}
                        className={`${styles.cell} ${isDiag ? styles.cellDiag : ''}`}
                        style={{ background: corrColor(val, isDiag) }}
                        onMouseEnter={(e) =>
                          !isDiag &&
                          setTooltip({
                            symA: rowSym,
                            symB: colSym,
                            val,
                            px: e.clientX + 12,
                            py: e.clientY - 10,
                          })
                        }
                        onMouseLeave={() => setTooltip(null)}
                      >
                        {isDiag ? '—' : val.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {tooltip && (
            <div className={styles.tooltip} style={{ left: tooltip.px, top: tooltip.py }}>
              <strong>
                {tooltip.symA} ↔ {tooltip.symB}
              </strong>
              : {tooltip.val.toFixed(2)}
              <br />
              {sectors[tooltip.symA]} / {sectors[tooltip.symB]}
              {sectors[tooltip.symA] === sectors[tooltip.symB] && ' (same sector)'}
            </div>
          )}
        </div>
      )}

      {/* ── Sector Exposure ── */}
      {sortedSectors.length > 0 && (
        <div className={styles.sectorSection}>
          <h3 className={styles.sectorTitle}>Sector Exposure</h3>
          <div className={styles.sectorBars}>
            {sortedSectors.map(([sec, { pct, value }]) => (
              <div key={sec} className={styles.sectorRow}>
                <span className={styles.sectorName}>{sec}</span>
                <div className={styles.sectorBarTrack}>
                  <div
                    className={styles.sectorBarFill}
                    style={{
                      width: `${(pct / maxPct) * 100}%`,
                      background: pct >= 40 ? '#ef4444' : pct >= 30 ? '#f59e0b' : sectorColor(sec),
                    }}
                  />
                  <div
                    className={styles.sectorBarThreshold}
                    style={{ left: `${(35 / maxPct) * 100}%` }}
                    title="35% rebalance threshold"
                  />
                </div>
                <span
                  className={styles.sectorPct}
                  style={{ color: pct >= 40 ? '#ef4444' : pct >= 30 ? '#f59e0b' : 'var(--text-primary)' }}
                >
                  {pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Cluster Groups ── */}
      {clusteredGroups.length > 0 && (
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 10px' }}>
            Correlated Clusters
          </h3>
          <div className={styles.clusters}>
            {clusteredGroups.map((group, i) => {
              const groupSector = sectors[group[0]] || '?';
              return (
                <div key={i} className={styles.cluster}>
                  <span className={styles.clusterLabel}>{groupSector}</span>
                  <div className={styles.clusterSymbols}>
                    {group.map((sym) => (
                      <span key={sym} className={styles.clusterChip}>
                        {sym}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioHeatmap;
